
import React from 'react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Crown, Calendar, Settings, CreditCard } from 'lucide-react';

const UserSubscriptionDetails: React.FC = () => {
  const { 
    subscribed, 
    subscriptionTier, 
    subscriptionEnd, 
    loading, 
    openCustomerPortal 
  } = useSubscription();

  if (loading) {
    return (
      <Card className="w-80">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Card className="w-80">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            Mi Suscripción
          </CardTitle>
          <Badge 
            variant={subscribed ? "default" : "secondary"}
            className={subscribed ? "bg-green-100 text-green-800" : ""}
          >
            {subscribed ? "Activa" : "Gratuita"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Plan:</span>
            <span className="font-medium">
              {subscriptionTier || "Plan Gratuito"}
            </span>
          </div>

          {subscribed && subscriptionEnd && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Renovación:
              </span>
              <span className="text-sm font-medium">
                {formatDate(subscriptionEnd)}
              </span>
            </div>
          )}
        </div>

        {subscribed ? (
          <div className="space-y-2">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800 font-medium">
                ✨ Acceso Premium Activo
              </p>
              <p className="text-xs text-green-600 mt-1">
                Disfruta de compresión ilimitada y sin marca de agua
              </p>
            </div>
            
            <Button
              onClick={openCustomerPortal}
              variant="outline"
              className="w-full flex items-center gap-2"
              size="sm"
            >
              <Settings className="w-4 h-4" />
              Gestionar Suscripción
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-medium">
                📈 Mejora tu Plan
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Obtén compresión ilimitada y funciones premium
              </p>
            </div>
            
            <Button
              variant="default"
              className="w-full flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              size="sm"
            >
              <CreditCard className="w-4 h-4" />
              Ver Planes Premium
            </Button>
          </div>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs text-gray-500 text-center">
            ¿Necesitas ayuda? Contacta con soporte
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserSubscriptionDetails;
